import React, { useState } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart2, Clock, CheckCircle, Users, Zap, Shield } from 'lucide-react';

const Section = styled.section`
  padding: 100px 24px;
  background-color: #f8f9fa;
  font-family: var(--font-sans);
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h2`
  font-size: 48px;
  color: #002147;
  margin-bottom: 16px;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #4a4a4a;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 60px;
  background: white;
  padding: 6px;
  border-radius: 16px;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  border: 1px solid #e0e0e0;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 32px;
  border-radius: 12px;
  border: none;
  background: ${props => props.active ? '#002147' : 'transparent'};
  color: ${props => props.active ? 'white' : '#64748b'};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-sans);

  &:hover {
    color: ${props => props.active ? 'white' : '#002147'};
    background: ${props => props.active ? '#002147' : 'rgba(0, 33, 71, 0.05)'};
  }
`;

const ContentContainer = styled.div`
  position: relative;
  min-height: 500px; // Fixed height to prevent layout shift
`;

const ContentWrapper = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: flex-start;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 40px;
    position: relative;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding-top: 20px;
`;

const ContentTitle = styled.h3`
  font-size: 32px;
  color: #002147;
  margin-bottom: 8px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
`;

const ContentDescription = styled.p`
  font-size: 18px;
  color: #4a4a4a;
  margin-bottom: 16px;
  line-height: 1.6;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FeatureItem = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const IconBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(0, 33, 71, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #002147;
  flex-shrink: 0;
`;

const FeatureTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 6px;
  letter-spacing: -0.01em;
`;

const FeatureDesc = styled.p`
  font-size: 16px;
  color: #4a4a4a;
  line-height: 1.5;
`;

const VisualContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.08);
  border: 1px solid #e0e0e0;
  height: 100%;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

// Improved Mockup Components
const ChatInterface = styled.div`
  width: 100%;
  max-width: 320px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  overflow: hidden;
  border: 1px solid #f0f0f0;
`;

const ChatHeader = styled.div`
  background: #002147;
  padding: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #002147;
  font-weight: bold;
  font-size: 12px;
`;

const ChatMessages = styled.div`
  padding: 20px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Message = styled.div<{ isUser?: boolean }>`
  background: ${props => props.isUser ? '#005f73' : 'white'};
  color: ${props => props.isUser ? 'white' : '#1e293b'};
  padding: 12px 16px;
  border-radius: 16px;
  border-bottom-right-radius: ${props => props.isUser ? '4px' : '16px'};
  border-bottom-left-radius: ${props => props.isUser ? '16px' : '4px'};
  font-size: 14px;
  line-height: 1.4;
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  max-width: 85%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const AdminDashboard = styled.div`
  width: 100%;
  background: white;
  border-radius: 16px;
  overflow: hidden;
`;

const DashStatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

const DashStat = styled.div`
  background: #f8fafc;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;

  div:first-of-type {
    font-size: 12px;
    color: #64748b;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }

  div:last-child {
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
  }
`;

const LeadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LeadItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  
  div {
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 500;
    color: #334155;
  }
`;

const StatusBadge = styled.span<{ status: 'hot' | 'warm' }>`
  padding: 4px 8px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.status === 'hot' ? '#dcfce7' : '#fef9c3'};
  color: ${props => props.status === 'hot' ? '#166534' : '#854d0e'};
`;

export const StudentVsAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'student' | 'admin'>('student');

  return (
    <Section>
      <Container>
        <Header>
          <Title>Two Sides of the Same Coin</Title>
          <Subtitle>
            Solve the frustration for your students AND your admissions team simultaneously.
          </Subtitle>
        </Header>

        <TabsContainer>
          <Tab active={activeTab === 'student'} onClick={() => setActiveTab('student')}>
            <Users size={18} />
            For Students
          </Tab>
          <Tab active={activeTab === 'admin'} onClick={() => setActiveTab('admin')}>
            <Shield size={18} />
            For Admissions Team
          </Tab>
        </TabsContainer>

        <ContentContainer>
          <AnimatePresence mode="wait">
            {activeTab === 'student' ? (
              <ContentWrapper
                key="student"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <TextContent>
                  <div>
                    <ContentTitle>Instant Answers, Anytime</ContentTitle>
                    <ContentDescription>
                      Students don't study 9-to-5. Give them the support they need, exactly when they need it.
                    </ContentDescription>
                  </div>

                  <FeatureList>
                    <FeatureItem>
                      <IconBox><Clock size={24} /></IconBox>
                      <div>
                        <FeatureTitle>24/7 Availability</FeatureTitle>
                        <FeatureDesc>Answer visa and enrollment questions at 2 AM on a Sunday.</FeatureDesc>
                      </div>
                    </FeatureItem>
                    <FeatureItem>
                      <IconBox><Zap size={24} /></IconBox>
                      <div>
                        <FeatureTitle>Instant Gratification</FeatureTitle>
                        <FeatureDesc>No waiting 24 hours for an email reply. Capture interest while it's hot.</FeatureDesc>
                      </div>
                    </FeatureItem>
                    <FeatureItem>
                      <IconBox><CheckCircle size={24} /></IconBox>
                      <div>
                        <FeatureTitle>Frictionless Enrollment</FeatureTitle>
                        <FeatureDesc>Guide them from "Just Browsing" to "Applied" in one conversation.</FeatureDesc>
                      </div>
                    </FeatureItem>
                  </FeatureList>
                </TextContent>

                <VisualContent>
                  <ChatInterface>
                    <ChatHeader>
                      <Avatar>AI</Avatar>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>Admissions Assistant</div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Online</div>
                      </div>
                    </ChatHeader>
                    <ChatMessages>
                      <Message isUser>Is the Level 5 course accredited?</Message>
                      <Message>Yes! It's Ofqual regulated and DEAC approved. This means it's recognized globally for teaching jobs.</Message>
                      <Message isUser>Great, can I pay in installments?</Message>
                      <Message>Absolutely. We offer a 3-month payment plan starting at €150/month. Would you like the link?</Message>
                    </ChatMessages>
                  </ChatInterface>
                </VisualContent>
              </ContentWrapper>
            ) : (
              <ContentWrapper
                key="admin"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <TextContent>
                  <div>
                    <ContentTitle>Filter Noise, Focus on Quality</ContentTitle>
                    <ContentDescription>
                      Stop your highly-paid admissions staff from answering "How much is the course?" all day.
                    </ContentDescription>
                  </div>

                  <FeatureList>
                    <FeatureItem>
                      <IconBox><BarChart2 size={24} /></IconBox>
                      <div>
                        <FeatureTitle>Automated Qualification</FeatureTitle>
                        <FeatureDesc>The AI filters out unqualified leads so your team only talks to serious students.</FeatureDesc>
                      </div>
                    </FeatureItem>
                    <FeatureItem>
                      <IconBox><Clock size={24} /></IconBox>
                      <div>
                        <FeatureTitle>Save 15+ Hours/Week</FeatureTitle>
                        <FeatureDesc>Eliminate repetitive email threads and focus on closing enrollments.</FeatureDesc>
                      </div>
                    </FeatureItem>
                    <FeatureItem>
                      <IconBox><CheckCircle size={24} /></IconBox>
                      <div>
                        <FeatureTitle>Seamless Handoff</FeatureTitle>
                        <FeatureDesc>When a student is ready to buy, the AI books a meeting for your team.</FeatureDesc>
                      </div>
                    </FeatureItem>
                  </FeatureList>
                </TextContent>

                <VisualContent>
                  <AdminDashboard>
                    <DashStatGrid>
                      <DashStat>
                        <div>Queries Automated</div>
                        <div>1,248</div>
                      </DashStat>
                      <DashStat>
                        <div>Hours Saved</div>
                        <div>42h</div>
                      </DashStat>
                    </DashStatGrid>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#64748b', marginBottom: '12px' }}>Recent Qualified Leads</div>
                    <LeadList>
                      <LeadItem>
                        <div>
                          <div style={{ width: '32px', height: '32px', background: '#e2e8f0', borderRadius: '50%' }} />
                          Sarah Jenkins
                        </div>
                        <StatusBadge status="hot">Ready to Enroll</StatusBadge>
                      </LeadItem>
                      <LeadItem>
                        <div>
                          <div style={{ width: '32px', height: '32px', background: '#e2e8f0', borderRadius: '50%' }} />
                          Michael Chen
                        </div>
                        <StatusBadge status="warm">Booked Demo</StatusBadge>
                      </LeadItem>
                    </LeadList>
                  </AdminDashboard>
                </VisualContent>
              </ContentWrapper>
            )}
          </AnimatePresence>
        </ContentContainer>
      </Container>
    </Section>
  );
};

export default StudentVsAdmin;
